import React, {useCallback} from 'react';
import {Alert, Box, Button, Modal, TextField, Typography} from '@mui/material';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import styles from '../../styles';
import {adminCreateCognitoUserForProfile} from '../../api/adminAPI';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Min 8 chars with at least 1 uppercase, 1 lowercase, 1 number, 1 special character.
const PASSWORD_PATTERN =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

type AddEmailForm = {
  email: string;
  tempPassword: string;
};

interface Props {
  profileId: number;
  onCreated: () => void;
}

const AddEmailModal: React.FC<Props> = ({profileId, onCreated}) => {
  const {t} = useTranslation();
  const [showDialog, setShowDialog] = React.useState(false);
  const [pending, setPending] = React.useState(false);
  const [submitError, setSubmitError] = React.useState('');
  const {handleSubmit, reset, control} = useForm<AddEmailForm>({
    defaultValues: {
      email: '',
      tempPassword: '',
    },
  });

  const handleOpenClick = useCallback(() => {
    setShowDialog(true);
  }, []);

  const handleDialogClose = useCallback(() => {
    reset();
    setPending(false);
    setSubmitError('');
    setShowDialog(false);
  }, [reset]);

  const handleFormSubmit = useCallback(
    async (data: AddEmailForm) => {
      setPending(true);
      setSubmitError('');
      try {
        const result = await adminCreateCognitoUserForProfile({
          profileId,
          email: data.email.trim(),
          tempPassword: data.tempPassword,
        });
        if (!result?.success) {
          setSubmitError(
            result?.error || (t('admin.profileDetail.addEmailError') as string),
          );
          setPending(false);
          return;
        }
        onCreated();
        handleDialogClose();
      } catch (e) {
        console.error(e);
        setSubmitError(t('admin.profileDetail.addEmailError') as string);
        setPending(false);
      }
    },
    [profileId, onCreated, handleDialogClose, t],
  );

  return (
    <>
      <Button variant="outlined" onClick={handleOpenClick}>
        {t('admin.profileDetail.addEmail')}
      </Button>
      <Modal
        open={showDialog}
        onClose={handleDialogClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description">
        <Box sx={styles.modal}>
          <Typography variant="h4" sx={{mb: 2}}>
            {t('admin.profileDetail.addEmail')}
          </Typography>
          {submitError && (
            <Alert
              severity="error"
              sx={{mb: 2}}
              onClose={() => setSubmitError('')}>
              {submitError}
            </Alert>
          )}
          <form onSubmit={handleSubmit(handleFormSubmit)} className="form">
            <Controller
              name="email"
              control={control}
              rules={{
                required: t('admin.profileDetail.invalidEmail') as string,
                pattern: {
                  value: EMAIL_PATTERN,
                  message: t('admin.profileDetail.invalidEmail') as string,
                },
              }}
              render={({field, fieldState: {error}}) => (
                <TextField
                  {...field}
                  label={t('general.email')}
                  type="email"
                  variant="outlined"
                  sx={{mb: 2}}
                  fullWidth
                  error={!!error}
                  helperText={error ? error.message : ''}
                />
              )}
            />
            <Controller
              name="tempPassword"
              control={control}
              rules={{
                required: t('admin.profileDetail.invalidPassword') as string,
                pattern: {
                  value: PASSWORD_PATTERN,
                  message: t('admin.profileDetail.invalidPassword') as string,
                },
              }}
              render={({field, fieldState: {error}}) => (
                <TextField
                  {...field}
                  label={t('admin.profileDetail.passwordLabel')}
                  type="text"
                  variant="outlined"
                  sx={{mb: 2}}
                  fullWidth
                  error={!!error}
                  helperText={error ? error.message : ''}
                />
              )}
            />
            <Button disabled={pending} type="submit">
              {t('general.submit')}
            </Button>
          </form>
        </Box>
      </Modal>
    </>
  );
};

export default React.memo(AddEmailModal);
